/**
 * JournifiSync Real-Time Watcher
 * 
 * Monitors the JournifiSync folder for new executions
 * and automatically imports trades as they happen.
 * Uses Electron IPC to communicate with main process.
 */

// Default sync folder path - ORIGINAL WORKING PATH
const DEFAULT_SYNC_FOLDER = 'C:\\NinjaTrader8Data\\JournifiSync';

/**
 * Check if running in Electron environment
 */
const isElectron = () => {
  return typeof window !== 'undefined' && window.electronAPI?.isElectron;
};

/**
 * JournifiSync Watcher Class
 * Uses main process file watcher via IPC
 */
class JournifiSyncWatcher {
  constructor(options = {}) {
    this.folderPath = options.folderPath || DEFAULT_SYNC_FOLDER;
    this.onNewExecutions = options.onNewExecutions || (() => {});
    this.onError = options.onError || console.error;
    this.onStatusChange = options.onStatusChange || (() => {});

    this.isWatching = false;
    this.ipcListener = null;
    // Cleanup function returned by the preload bridge's onNewExecutions
    // subscription. Stashed here so we can remove the previous listener
    // before adding a new one — without it, a stop/start cycle (or a
    // DevTools soft-reload that keeps this singleton alive) stacks
    // multiple listeners on the same IPC channel and the renderer
    // receives N copies of every execution batch.
    this.ipcCleanup = null;
  }

  /**
   * Start watching for new executions via main process
   */
  async start() {
    if (!isElectron()) {
      this.onError('JournifiSync watcher requires Electron environment');
      return false;
    }

    try {
      // Tear down any previous listener before subscribing again. Critical
      // for: (1) stop+restart within the same renderer session, and (2)
      // DevTools soft-reload where the React tree is replaced but this
      // module-scope singleton survives — the OLD listener would still fire
      // into the dead React tree.
      if (typeof this.ipcCleanup === 'function') {
        try { this.ipcCleanup(); } catch { /* listener already gone */ }
        this.ipcCleanup = null;
      }

      // Set up listener for new executions from main process. The wrapper
      // reads `this.onNewExecutions` at FIRE time so updating callbacks via
      // getWatcher({ onNewExecutions }) takes effect without re-subscribing.
      this.ipcListener = (data) => {
        console.log('[JournifiSync] Received new executions:', data.length);
        this.onNewExecutions(data);
      };

      // The preload bridge returns a cleanup function — capture it so stop()
      // and the next start() can remove this exact listener.
      this.ipcCleanup = window.electronAPI.journifiSync.onNewExecutions(this.ipcListener);

      // Start the watcher in main process
      const result = await window.electronAPI.journifiSync.watchFolder(this.folderPath);

      if (!result.success) {
        this.onError(result.error);
        return false;
      }

      this.isWatching = true;
      this.onStatusChange({ watching: true, folderPath: this.folderPath });

      console.log('[JournifiSync] Watcher started:', this.folderPath);
      return true;

    } catch (error) {
      this.onError(`Failed to start watcher: ${error.message}`);
      return false;
    }
  }

  /**
   * Stop watching
   */
  async stop() {
    if (!isElectron()) return;

    // Remove the IPC listener so the next start() doesn't double-stack.
    // Also matters for soft-reload scenarios where the singleton survives.
    if (typeof this.ipcCleanup === 'function') {
      try { this.ipcCleanup(); } catch { /* already gone */ }
      this.ipcCleanup = null;
    }
    this.ipcListener = null;

    try {
      await window.electronAPI.journifiSync.stopWatch();
    } catch (error) {
      console.error('[JournifiSync] Error stopping watcher:', error);
    }

    this.isWatching = false;
    this.onStatusChange({ watching: false });
    console.log('[JournifiSync] Watcher stopped');
  }

  /**
   * Get watcher status
   */
  getStatus() {
    return {
      isWatching: this.isWatching,
      folderPath: this.folderPath
    };
  }
}

// Singleton instance for app-wide use
let watcherInstance = null;

/**
 * Get or create the watcher instance
 * Always updates callbacks so a stop+restart uses fresh callbacks.
 */
export const getWatcher = (options = {}) => {
  if (!watcherInstance) {
    watcherInstance = new JournifiSyncWatcher(options);
  } else {
    // Update callbacks on existing instance (critical for stop+restart within same session)
    if (options.onNewExecutions) watcherInstance.onNewExecutions = options.onNewExecutions;
    if (options.onError) watcherInstance.onError = options.onError;
    if (options.onStatusChange) watcherInstance.onStatusChange = options.onStatusChange;
    if (options.folderPath) watcherInstance.folderPath = options.folderPath;
  }
  return watcherInstance;
};

/**
 * Start the watcher with callbacks
 */
export const startWatching = async (options = {}) => {
  const watcher = getWatcher(options);
  return watcher.start();
};

/**
 * Stop the watcher
 */
export const stopWatching = async () => {
  if (watcherInstance) {
    await watcherInstance.stop();
  }
};

/**
 * Check if watcher is active
 */
export const isWatching = () => {
  return watcherInstance?.isWatching || false;
};

export default JournifiSyncWatcher;
